import type { Types } from '@common/models';
import { tuneSubscribe } from '@common/talkn-config';
import Sequence from '@common/Sequence';

type Action = {
  type: string;
  tuneCh?: Types['Ch'];
  rank?: Types['Rank'][];
  posts?: Types['Post'][];
  liveRevision?: number;
  tuneConnection?: string;
};

const getLiveRevision = (entry?: { liveRevision?: number }): number => Number(entry?.liveRevision ?? 0);
const getActionLiveRevision = (action: Action): number => Number(action.liveRevision ?? action.tuneCh?.liveRevision ?? 0);

const isStaleLiveUpdate = (state: Types['Rank'][], action: Action): boolean => {
  const liveRevision = getActionLiveRevision(action);
  const tuneConnection = action.tuneConnection ?? action.tuneCh?.connection;
  if (!liveRevision || !tuneConnection) return false;
  const existing = state.find((entry) => entry.connection === tuneConnection);
  return getLiveRevision(existing) > liveRevision;
};

const preserveFreshEntries = (state: Types['Rank'][], next: Types['Rank'][], action: Action): Types['Rank'][] => {
  const actionRevision = getActionLiveRevision(action);
  const nextConnections = new Set(next.map((entry) => entry.connection));
  const merged = next.map((entry) => {
    const existing = state.find((current) => current.connection === entry.connection);
    return getLiveRevision(existing) > getLiveRevision(entry) ? existing! : entry;
  });
  for (const existing of state) {
    if (!nextConnections.has(existing.connection) && getLiveRevision(existing) > actionRevision) {
      merged.push(existing);
    }
  }
  return merged;
};

export default (state: Types['Rank'][] = [], action: Action) => {
  const { posts, rank, tuneCh } = action;
  let postsCnt = posts?.length || 0;
  let rankCnt = rank?.length || 0;

  const sortLiveCnt = (a: Types['Rank'], b: Types['Rank']) => {
    if (a.liveCnt < b.liveCnt) return 1;
    if (a.liveCnt > b.liveCnt) return -1;
    return 0;
  };

  switch (action.type) {
    case 'SERVER_TO_API[EMIT]:fetchPosts':
      if (tuneCh && posts) {
        if (postsCnt === 0) return state;

        return state.map((rank: Types['Rank']) => {
          if (tuneCh.connection === rank.connection) {
            return {
              ...rank,
//              favicon: posts[postsCnt - 1].favicon,
              stampId: posts[postsCnt - 1].stampId,
              post: posts[postsCnt - 1].content,
            };
          } else {
            return rank;
          }
        });
      }
      return state;
    case 'SERVER_TO_API[BROADCAST]:tune':
    case 'SERVER_TO_API[BROADCAST]:changeThread':
    case 'SERVER_TO_API[BROADCAST]:untune':
    case `${Sequence.SERVER_TO_API_BROADCAST}${tuneSubscribe.stateRank}`:
      if (!tuneCh) {
        return state;
      }
      if (rank) {
        if (isStaleLiveUpdate(state, action)) return state;
        // Preserve existing meta on broadcast updates.
        const metaMap = new Map<string, Types['ChDetail'] | undefined>();
        state.forEach((entry: any) => {
          if (entry?.connection && entry?.meta) {
            metaMap.set(entry.connection, entry.meta);
          }
        });
        const nextState = rank.map((entry: any) => {
          const meta = metaMap.get(entry?.connection);
          return meta && !entry?.meta ? { ...entry, meta } : entry;
        });
        return preserveFreshEntries(state, nextState, action);
      }
      return state;
    case `${Sequence.SERVER_TO_API_BROADCAST}${tuneSubscribe.statePosts}`:
      if (posts && posts.length > 0 && posts[0]) {
        const postConnection = posts[0].connection;
        return state.map((rank: Types['Rank']) => {
          const isRootTune = tuneCh && rank.connection === tuneCh.connection;
          if (postConnection === rank.connection || (isRootTune && postConnection.startsWith(rank.connection))) {
            return { ...rank, latestPost: posts[0] };
          }
          return rank;
        });
      }
      return state;
    case 'SERVER_TO_API[EMIT]:fetchRank':
    case 'SERVER_TO_API[EMIT]:rank':
      // stateとaction.rankの両方存在する場合
      if (state && state.length > 0 && rank && rankCnt > 0) {
        const newRanks = [];
        let lastPost = rank[0];
        for (let i = 0; i < rankCnt; i++) {
          let newRank = rank[i];
          lastPost = newRank.updateTime > lastPost.updateTime ? newRank : lastPost;

          const existing = state.find((entry) => entry.connection === newRank.connection);
          if (existing) {
            newRank = {
              ...newRank,
              liveCnt: existing.liveCnt,
            };
          }
          newRanks.push(newRank);
        }

        newRanks.sort(sortLiveCnt);
        if (newRanks[0]) {
          newRanks[0].content = lastPost.content;
          newRanks[0].stampId = lastPost.stampId;
        }
        return newRanks;
      } else {
        return action.rank ? action.rank : state;
      }
    default:
      if (action.rank) {
        if (isStaleLiveUpdate(state, action)) return state;
        return preserveFreshEntries(state, action.rank, action);
      }
      return state;
  }
};
